import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { AIBrainLoader, SkeletonCard, NeuralPulseOrb } from '../components/ui/ai-loading';
import { toast } from 'sonner';
import {
  Search, Users, Shield, CheckCircle, AlertTriangle, MapPin,
  Video, User, Filter, X, Handshake, ArrowLeft, Sparkles, Activity,
  Compass, Heart, MessageSquare, FileText, BookOpen, Settings, Calendar, Wand2,
  BookmarkCheck, Megaphone, Columns3, Loader2, BadgeCheck, CreditCard, IdCard
} from 'lucide-react';
import { NegotiateModal } from '../components/brand/NegotiateModal';
import { DashboardShell } from '../components/brand/DashboardShell';
import { BentoGrid, BentoTile, RingScore, TileHeader } from '../components/brand/BentoWidgets';

const API = process.env.REACT_APP_BACKEND_URL;

const commonDiagnoses = [
  'Type 2 Diabetes', 'Hypertension', 'COPD', 'CHF', 'Dementia',
  'Parkinson\'s Disease', 'Stroke Recovery', 'Cancer', 'Arthritis',
  'Chronic Kidney Disease', 'Post-Surgical Care', 'Fall Recovery'
];

const commonComorbidities = [
  'Diabetes', 'Hypertension', 'Heart Disease', 'COPD', 'Arthritis',
  'Obesity', 'Depression', 'Anxiety', 'Chronic Pain', 'Kidney Disease'
];

// Hero quick-start chips → concrete filter patches so a chip + state always
// yields a working search (primary_diagnosis is required by the matcher).
const heroChips = [
  { key: 'In-Home Care', patch: { primary_diagnosis: 'Post-Surgical Care', preferred_visit_type: 'in_person', is_hospice: false } },
  { key: 'Hospice', patch: { primary_diagnosis: 'Cancer', is_hospice: true, preferred_visit_type: 'in_person' } },
  { key: 'Telehealth', patch: { primary_diagnosis: 'CHF', preferred_visit_type: 'telehealth', is_hospice: false } },
  { key: 'Specialty Therapy', patch: { primary_diagnosis: 'Stroke Recovery', preferred_visit_type: 'in_person', is_hospice: false } },
  { key: 'Post-Surgical', patch: { primary_diagnosis: 'Post-Surgical Care', preferred_visit_type: 'in_person', is_hospice: false } },
  { key: 'Dementia Care', patch: { primary_diagnosis: 'Dementia', preferred_visit_type: 'in_person', is_hospice: false } },
];

const states = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

export default function FindClinicians() {
  const { getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [negotiateTarget, setNegotiateTarget] = useState(null);
  const [lynkSuggestion, setLynkSuggestion] = useState(null);
  const [suggestionApplied, setSuggestionApplied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  const [formData, setFormData] = useState({
    primary_diagnosis: '',
    comorbidities: [],
    adl_dependency_level: 0,
    fall_risk: 'low',
    medication_count: 0,
    is_hospice: false,
    requires_hands_on_care: false,
    state: '',
    preferred_visit_type: 'in_person',
    language_preference: 'English',
    min_score_threshold: 75,
    include_below_threshold: false,
    in_network_only: false,
    max_distance_miles: null,
  });

  const [comorbidityInput, setComorbidityInput] = useState('');

  // Fetch Cara memory-driven suggestions on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/lynk/memory/suggest-search`, getAuthHeader())
      .then((r) => {
        const hints = r.data.hints || {};
        if (Object.keys(hints).length > 0 && r.data.reason) {
          setLynkSuggestion({ hints, reason: r.data.reason });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch the patient's saved/shortlisted clinician ids (drives the heart state)
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/patient/saved/ids`, getAuthHeader())
      .then((r) => setSavedIds(new Set(r.data.clinician_ids || [])))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggleSave = async (clinicianId) => {
    // optimistic toggle
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clinicianId)) next.delete(clinicianId); else next.add(clinicianId);
      return next;
    });
    try {
      const { data } = await axios.post(`${API}/api/patient/saved/${clinicianId}`, {}, getAuthHeader());
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (data.saved) next.add(clinicianId); else next.delete(clinicianId);
        return next;
      });
      toast.success(data.saved ? 'Saved to your shortlist' : 'Removed from your shortlist');
    } catch (e) {
      // revert on failure
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (next.has(clinicianId)) next.delete(clinicianId); else next.add(clinicianId);
        return next;
      });
      toast.error('Could not update your shortlist');
    }
  };

  // Prefill + auto-run from a saved search ("Run again")
  useEffect(() => {
    const pf = location.state?.prefillFilters;
    if (pf) {
      setFormData((prev) => ({ ...prev, ...pf }));
      setSaved(true); // already a saved search
      if (location.state?.autoRun) {
        setTimeout(() => runSearch(pf), 50);
      }
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyLynkSuggestion = () => {
    if (!lynkSuggestion) return;
    const h = lynkSuggestion.hints;
    setFormData((prev) => ({
      ...prev,
      ...(h.primary_diagnosis ? { primary_diagnosis: h.primary_diagnosis } : {}),
      ...(h.preferred_visit_type ? { preferred_visit_type: h.preferred_visit_type } : {}),
      ...(h.fall_risk ? { fall_risk: h.fall_risk } : {}),
      ...(typeof h.is_hospice === 'boolean' ? { is_hospice: h.is_hospice } : {}),
      ...(Array.isArray(h.comorbidities) && h.comorbidities.length
        ? { comorbidities: Array.from(new Set([...(prev.comorbidities || []), ...h.comorbidities])) }
        : {}),
    }));
    setSuggestionApplied(true);
    toast.success('Cara pre-filled what we know about you. Adjust freely.');
  };

  const addComorbidity = () => {
    if (comorbidityInput && !formData.comorbidities.includes(comorbidityInput)) {
      setFormData((p) => ({ ...p, comorbidities: [...p.comorbidities, comorbidityInput] }));
      setComorbidityInput('');
    }
  };
  const removeComorbidity = (item) => {
    setFormData((p) => ({ ...p, comorbidities: p.comorbidities.filter(c => c !== item) }));
  };

  const handleSearch = async () => { await runSearch(); };

  const runSearch = async (overrideForm) => {
    const data = overrideForm || formData;
    // Iter 163 · Precise error copy — the old "enter primary diagnosis
    // AND state" toast fired even when only one field was missing,
    // which was confusing after a hero chip (which sets primary_diagnosis)
    // had been clicked but the state Select still hadn't been picked.
    if (!data.primary_diagnosis && !data.state) {
      toast.error('Pick a care need above and your state to start');
      return;
    }
    if (!data.primary_diagnosis) {
      toast.error('Pick a care need above (or open Smart Filters)');
      return;
    }
    if (!data.state) {
      toast.error('Pick your state so we can find local clinicians');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/matching/find-clinicians`, data, getAuthHeader());
      setResults(response.data);
      setCompareIds([]);
      if (!overrideForm) setSaved(false);
      toast.success(`Found ${response.data.matches?.length || 0} matching clinicians`);
    } catch (error) {
      toast.error('Failed to find matches');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveSearch = async () => {
    if (!formData.primary_diagnosis || !formData.state) {
      toast.error('Run a search first, then save it');
      return;
    }
    setSaving(true);
    try {
      const name = `${formData.primary_diagnosis} · ${formData.state}`;
      await axios.post(`${API}/api/saved-searches`, { name, filters: formData }, getAuthHeader());
      setSaved(true);
      toast.success("Saved! We'll alert you when new clinicians match.");
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save search');
    } finally {
      setSaving(false);
    }
  };

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) { toast.error('Compare up to 3 clinicians at a time'); return prev; }
      return [...prev, id];
    });
  };

  const compareList = (results?.matches || []).filter((m) => compareIds.includes(m.clinician_id));

  const allMatches = results?.matches || [];
  const visibleMatches = formData.in_network_only ? allMatches.filter((m) => m.in_network) : allMatches;

  const getScoreClass = (score) => {
    if (score >= 90) return 'bg-cn-success/10 text-cn-success';
    if (score >= 80) return 'bg-cn-primary/10 text-cn-primary';
    if (score >= 75) return 'bg-cn-warning/15 text-cn-warning';
    return 'bg-cn-error/10 text-cn-error';
  };
  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-cn-success';
      case 'moderate': return 'text-cn-warning';
      case 'high': return 'text-cn-error';
      default: return 'text-cn-text-muted';
    }
  };

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/patient/dashboard' },
    { icon: Compass, label: 'Care Navigator', href: '/patient/navigator', highlight: true },
    { icon: Users, label: 'Find Clinicians', active: true },
    { icon: BookmarkCheck, label: 'Saved Searches', href: '/patient/saved-searches' },
    { icon: Heart, label: 'Saved Clinicians', href: '/patient/saved-clinicians' },
    { icon: Megaphone, label: 'Post a Need', href: '/patient/care-requests' },
    { icon: BookOpen, label: 'Browse Marketplace', href: '/marketplace' },
    { icon: Handshake, label: 'Offers & Bookings', href: '/patient/offers' },
    { icon: Calendar, label: 'Appointments', href: '/patient/dashboard#appointments' },
    { icon: Heart, label: 'Daily Check-in', href: '/patient/checkin' },
    { icon: Users, label: 'Family Portal', href: '/patient/family' },
    { icon: FileText, label: 'Documents', href: '/patient/documents' },
    { icon: MessageSquare, label: 'Messages', href: '/patient/messages' },
    { icon: Settings, label: 'Settings', href: '#' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Find your care team"
      subtitle="Tell us what you need — we'll surface the best-fit clinicians, ranked by safety and clinical fit."
      rightSlot={
        results && (
          <Button onClick={() => setResults(null)} className="cn-btn cn-btn-ghost" data-testid="new-search-btn">
            <Filter className="w-4 h-4" /> New Search
          </Button>
        )
      }
      testId="find-clinicians"
    >
      {/* Phase A — hero search band */}
      <div className="mb-6 cn-card p-5 sm:p-6 border-transparent text-white" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #334155 60%, #334155 100%)' }} data-testid="findclin-hero-search">
        <p className="text-[11px] uppercase tracking-[0.14em] text-white/70 font-bold">I need help with…</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {heroChips.map((chip) => {
            const active = formData.primary_diagnosis === chip.patch.primary_diagnosis
              && (chip.key !== 'Hospice' || formData.is_hospice)
              && (chip.key !== 'Telehealth' || formData.preferred_visit_type === 'telehealth');
            return (
              <button
                key={chip.key}
                onClick={() => setFormData((p) => ({ ...p, ...chip.patch }))}
                data-testid={`hero-chip-${chip.key.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${active ? 'bg-white text-cn-primary shadow' : 'bg-white/15 hover:bg-white/25 text-white'}`}
              >{chip.key}</button>
            );
          })}
        </div>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <Select value={formData.state} onValueChange={(v) => setFormData((p) => ({ ...p, state: v }))}>
              <SelectTrigger data-testid="hero-state" className="h-11 px-4 rounded-2xl bg-white text-cn-text text-sm border-0 shadow-none focus:ring-0">
                <SelectValue placeholder="Your state…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {states.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSearch} disabled={loading} className="cn-btn !bg-white !text-cn-primary hover:!bg-cn-bg !h-11 !px-6" data-testid="hero-search-btn">
            <Search className="w-4 h-4" />
            {loading ? 'Searching…' : 'Find care now'}
          </Button>
        </div>
        <button
          onClick={() => navigate('/patient/care-requests')}
          data-testid="hero-post-need"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white/85 hover:text-white"
        >
          <Megaphone className="w-3.5 h-3.5" /> Or post a need and let clinicians come to you →
        </button>
      </div>

      {/* Trust band */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-2xl bg-cn-success/8 border border-cn-success/20" data-testid="findclin-trust-band">
        <div className="flex flex-wrap items-center gap-2 text-xs text-cn-text">
          <Shield className="w-4 h-4 text-cn-success" />
          <span className="font-semibold">Every CaraLoom clinician is</span>
          <Badge className="bg-white border border-cn-success/30 text-cn-success">license-verified</Badge>
          <Badge className="bg-white border border-cn-success/30 text-cn-success">background-checked</Badge>
          <Badge className="bg-white border border-cn-success/30 text-cn-success">insured</Badge>
        </div>
        <Link to="/trust-safety" className="text-xs font-bold text-cn-primary hover:underline" data-testid="trust-band-link">How we verify →</Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
        {/* SMART FILTERS — sticky */}
        <div className="lg:col-span-4 xl:col-span-3">
          <div className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-6 lg:sticky lg:top-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="cn-eyebrow">Smart filters</p>
                <h3 className="cn-display text-lg">Care requirements</h3>
              </div>
            </div>

            {lynkSuggestion && !suggestionApplied && (
              <div className="mb-4 p-3.5 rounded-2xl border border-cn-primary/25 bg-cn-primary/8" data-testid="lynk-memory-suggestion">
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
                    <Wand2 className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-cn-primary font-bold">Cara remembers</p>
                    <p className="text-xs text-cn-text dark:text-white mt-0.5 leading-relaxed">{lynkSuggestion.reason}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={applyLynkSuggestion} data-testid="lynk-apply-suggestion" className="text-[11px] font-bold text-white px-3 py-1.5 rounded-full" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
                        Pre-fill for me
                      </button>
                      <button onClick={() => setLynkSuggestion(null)} data-testid="lynk-dismiss-suggestion" className="text-[11px] font-semibold text-cn-text-muted hover:text-cn-text">
                        No thanks
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Field label="Primary diagnosis *">
                <Select value={formData.primary_diagnosis} onValueChange={(v) => setFormData((p) => ({ ...p, primary_diagnosis: v }))}>
                  <SelectTrigger data-testid="filter-diagnosis"><SelectValue placeholder="Select diagnosis" /></SelectTrigger>
                  <SelectContent>{commonDiagnoses.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </Field>

              <Field label="State *">
                <Select value={formData.state} onValueChange={(v) => setFormData((p) => ({ ...p, state: v }))}>
                  <SelectTrigger data-testid="filter-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>

              <Field label="Visit preference">
                <div className="grid grid-cols-3 gap-1.5 p-1 bg-cn-bg rounded-2xl border border-cn-border/60">
                  {['in_person', 'telehealth', 'hybrid'].map((v) => (
                    <button
                      key={v}
                      onClick={() => setFormData((p) => ({ ...p, preferred_visit_type: v }))}
                      data-testid={`visit-${v}`}
                      className={`text-xs font-semibold py-2 rounded-xl capitalize transition-all ${
                        formData.preferred_visit_type === v ? 'bg-white shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'
                      }`}
                    >
                      {v.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </Field>

              {formData.preferred_visit_type !== 'telehealth' && (
                <Field label="Search radius">
                  <Select
                    value={formData.max_distance_miles == null ? 'any' : String(formData.max_distance_miles)}
                    onValueChange={(v) => setFormData((p) => ({ ...p, max_distance_miles: v === 'any' ? null : parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="filter-radius"><SelectValue placeholder="Any distance" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any distance</SelectItem>
                      <SelectItem value="10">Within 10 miles</SelectItem>
                      <SelectItem value="25">Within 25 miles</SelectItem>
                      <SelectItem value="50">Within 50 miles</SelectItem>
                      <SelectItem value="100">Within 100 miles</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <Field label="Additional conditions">
                <div className="flex gap-2">
                  <Select value={comorbidityInput} onValueChange={setComorbidityInput}>
                    <SelectTrigger className="flex-1" data-testid="filter-comorbidity"><SelectValue placeholder="Add condition" /></SelectTrigger>
                    <SelectContent>
                      {commonComorbidities.filter(c => !formData.comorbidities.includes(c)).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={addComorbidity} className="cn-btn cn-btn-ghost !py-2 !px-3" data-testid="add-comorbidity">+</Button>
                </div>
                {formData.comorbidities.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.comorbidities.map(c => (
                      <Badge key={c} className="bg-cn-primary/10 text-cn-primary cursor-pointer" onClick={() => removeComorbidity(c)}>
                        {c} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="ADL (0-6)">
                  <Input type="number" min={0} max={6}
                    value={formData.adl_dependency_level}
                    onChange={(e) => setFormData((p) => ({ ...p, adl_dependency_level: parseInt(e.target.value) || 0 }))}
                    data-testid="filter-adl"
                  />
                </Field>
                <Field label="Medications">
                  <Input type="number" min={0}
                    value={formData.medication_count}
                    onChange={(e) => setFormData((p) => ({ ...p, medication_count: parseInt(e.target.value) || 0 }))}
                    data-testid="filter-meds"
                  />
                </Field>
              </div>

              <Field label="Fall risk">
                <div className="grid grid-cols-3 gap-1.5 p-1 bg-cn-bg rounded-2xl border border-cn-border/60">
                  {['low', 'moderate', 'high'].map((v) => (
                    <button
                      key={v}
                      onClick={() => setFormData((p) => ({ ...p, fall_risk: v }))}
                      data-testid={`fall-${v}`}
                      className={`text-xs font-semibold py-2 rounded-xl capitalize transition-all ${
                        formData.fall_risk === v ? 'bg-white shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'
                      }`}
                    >{v}</button>
                  ))}
                </div>
              </Field>

              <div className="space-y-2 pt-2 border-t border-cn-border/60">
                {[
                  { key: 'is_hospice', label: 'Hospice / Palliative care' },
                  { key: 'requires_hands_on_care', label: 'Requires hands-on care' },
                  { key: 'in_network_only', label: 'In-network with my insurance only' },
                ].map((opt) => (
                  <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData[opt.key]}
                      onCheckedChange={(c) => setFormData((p) => ({ ...p, [opt.key]: c }))}
                      data-testid={`toggle-${opt.key}`}
                    />
                    <span className="text-sm text-cn-text">{opt.label}</span>
                  </label>
                ))}
              </div>

              <Button onClick={handleSearch} disabled={loading} className="cn-btn cn-btn-primary w-full mt-2 !py-3" data-testid="search-clinicians-btn">
                {loading ? 'Finding matches...' : <><NeuralPulseOrb size={18} /> Find Clinicians</>}
              </Button>
            </div>
          </div>
        </div>

        {/* MAGAZINE GRID — results */}
        <div className="lg:col-span-8 xl:col-span-9">
          {loading ? (
            <div className="space-y-5">
              <AIBrainLoader message="Finding your perfect care matches..." />
              <div className="grid sm:grid-cols-2 gap-4">{[1,2,3,4].map(i => <SkeletonCard key={i} />)}</div>
            </div>
          ) : results ? (
            <div className="space-y-5">
              {/* Assessment summary */}
              <div className="cn-card p-6 text-white relative overflow-hidden border-transparent" data-testid="assessment-summary"
                style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 55%, #334155 100%)' }}>
                <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-cn-accent/20 blur-3xl" />
                <div className="relative grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/70 font-bold">Complexity</p>
                    <p className="font-display font-extrabold text-3xl mt-1">{results.patient_assessment?.complexity_score || 0}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/70 font-bold">Risk</p>
                    <p className="font-display font-bold text-lg mt-1 capitalize">{results.patient_assessment?.risk_category?.replace('_', ' ') || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/70 font-bold">License needed</p>
                    <p className="font-display font-bold text-lg mt-1">{results.patient_assessment?.required_license_level || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/70 font-bold">Visit type</p>
                    <p className="font-display font-bold text-lg mt-1 flex items-center gap-1">
                      {results.patient_assessment?.telehealth_eligible ? <><Video className="w-4 h-4" /> Telehealth OK</> : <><User className="w-4 h-4" /> In-person</>}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="cn-display text-2xl text-cn-text dark:text-white">{visibleMatches.length} matching clinicians{formData.in_network_only ? ' · in-network' : ''}</h2>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-cn-text-muted hidden sm:inline">Processed in {results.processing_time_ms?.toFixed(1)}ms</span>
                  <Button
                    onClick={saveSearch}
                    disabled={saving || saved}
                    className={`cn-btn ${saved ? 'cn-btn-ghost text-cn-success' : 'cn-btn-ghost'} !py-2`}
                    data-testid="save-search-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4" /> : <BookmarkCheck className="w-4 h-4" />}
                    {saved ? 'Saved & alerting' : 'Save this search & alert me'}
                  </Button>
                </div>
              </div>

              {visibleMatches.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-5">
                  {visibleMatches.map((match, index) => {
                    const featured = index === 0;
                    return (
                      <div
                        key={match.match_id}
                        className={`cn-card cn-card-hover bg-white dark:bg-[#0F172A] border border-cn-border/70 overflow-hidden animate-fade-in-up stagger-${(index % 5) + 1} ${featured ? 'sm:col-span-2 ring-1 ring-cn-primary/30' : ''}`}
                        data-testid={`match-card-${index}`}
                      >
                        {featured && (
                          <div className="px-4 py-1.5 bg-gradient-to-r from-cn-primary to-cn-secondary text-white text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" /> Best match for you
                          </div>
                        )}
                        <div className="p-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className={`rounded-2xl bg-gradient-to-br from-cn-primary to-cn-secondary text-white flex items-center justify-center font-bold ${featured ? 'w-16 h-16 text-2xl' : 'w-12 h-12 text-lg'}`}>
                                {match.clinician_name?.charAt(0) || 'C'}
                              </div>
                              <div>
                                <h3 className="font-display font-bold text-cn-text dark:text-white">{match.clinician_name || 'Clinician'}</h3>
                                <div className="flex items-center gap-2 text-xs text-cn-text-muted mt-0.5">
                                  <Badge variant="outline" className="border-cn-border">{match.license_type || 'RN'}</Badge>
                                  {match.years_experience > 0 && <span>{match.years_experience} yrs exp</span>}
                                </div>
                              </div>
                            </div>
                            <div className={`text-center px-4 py-2 rounded-2xl ${getScoreClass(match.total_score)}`}>
                              <p className="font-display font-extrabold text-2xl leading-none">{match.total_score}%</p>
                              <p className="text-[10px] uppercase tracking-wider font-bold mt-0.5">Match</p>
                            </div>
                          </div>

                          <div className="mt-4 space-y-2">
                            {[
                              { l: 'Clinical fit', v: match.clinical_fit_score },
                              { l: 'Complexity', v: match.complexity_alignment_score },
                              { l: 'Availability', v: match.availability_score },
                            ].map((row) => (
                              <div key={row.l} className="flex items-center gap-2 text-xs">
                                <span className="w-20 text-cn-text-muted font-semibold">{row.l}</span>
                                <Progress value={row.v} className="h-1.5 flex-1" />
                                <span className="w-9 text-right font-mono text-cn-text">{row.v}%</span>
                              </div>
                            ))}
                          </div>

                          <div className="mt-4 flex flex-wrap items-center gap-2">
                            {match.safety_gates_passed ? (
                              <Badge className="bg-cn-success/10 text-cn-success border border-cn-success/20">
                                <Shield className="w-3 h-3 mr-1" /> Safety checks passed
                              </Badge>
                            ) : (
                              <Badge className="bg-cn-error/10 text-cn-error border border-cn-error/20">
                                <AlertTriangle className="w-3 h-3 mr-1" /> Review required
                              </Badge>
                            )}
                            <Badge variant="outline" className={`${getRiskColor(match.risk_level)} border-current/30`}>{match.risk_level} risk</Badge>
                            {match.npi_verified && (
                              <Badge className="bg-sky-50 text-sky-800 border border-sky-700/20" data-testid={`npi-verified-badge-${index}`}>
                                <IdCard className="w-3 h-3 mr-1" /> NPI verified
                              </Badge>
                            )}
                            {match.distance_miles != null && (
                              <Badge variant="outline" className="border-cn-border text-cn-text-muted" data-testid={`distance-badge-${index}`}>
                                <MapPin className="w-3 h-3 mr-1" /> {match.distance_miles < 1 ? '< 1 mile away' : `${Math.round(match.distance_miles)} mi away`}
                              </Badge>
                            )}
                            {match.in_network ? (
                              <Badge className="bg-cn-primary/10 text-cn-primary border border-cn-primary/25" data-testid={`in-network-badge-${index}`}>
                                <BadgeCheck className="w-3 h-3 mr-1" /> In-network
                              </Badge>
                            ) : match.accepts_insurance ? (
                              <Badge variant="outline" className="border-cn-border text-cn-text-muted" data-testid={`accepts-insurance-badge-${index}`}>
                                <CreditCard className="w-3 h-3 mr-1" /> Accepts insurance
                              </Badge>
                            ) : null}
                          </div>

                          {match.experience_highlights?.length > 0 && (
                            <ul className="mt-4 space-y-1.5">
                              {match.experience_highlights.slice(0, featured ? 4 : 2).map((h, i) => (
                                <li key={i} className="text-xs text-cn-text-muted flex items-start gap-2">
                                  <CheckCircle className="w-3.5 h-3.5 text-cn-success mt-0.5 flex-shrink-0" /> {h}
                                </li>
                              ))}
                            </ul>
                          )}

                          {match.match_explanation && (
                            <p className="mt-4 text-sm italic text-cn-text-muted border-l-2 border-cn-primary/40 pl-3">
                              &ldquo;{match.match_explanation}&rdquo;
                            </p>
                          )}

                          <div className="mt-5 flex gap-2">
                            <Button
                              className="flex-1 cn-btn cn-btn-primary !py-2"
                              onClick={() => setNegotiateTarget({ id: match.clinician_id, first_name: match.clinician_name })}
                              title="Propose your rate, schedule and visit details — the clinician can accept or counter before anything is booked."
                              data-testid={`negotiate-btn-${index}`}
                            >
                              <Handshake className="w-4 h-4" /> Negotiate & Book
                            </Button>
                            <Button
                              className={`cn-btn !py-2 ${compareIds.includes(match.clinician_id) ? 'cn-btn-primary' : 'cn-btn-ghost'}`}
                              onClick={() => toggleCompare(match.clinician_id)}
                              title="Add to compare"
                              data-testid={`compare-toggle-${index}`}
                            >
                              <Columns3 className="w-4 h-4" />
                            </Button>
                            <Button
                              className={`cn-btn cn-btn-ghost !py-2 ${savedIds.has(match.clinician_id) ? 'text-cn-terracotta-500' : ''}`}
                              onClick={() => toggleSave(match.clinician_id)}
                              title={savedIds.has(match.clinician_id) ? 'Remove from shortlist' : 'Save to shortlist'}
                              aria-pressed={savedIds.has(match.clinician_id)}
                              data-testid={`save-match-${index}`}
                            >
                              <Heart className={`w-4 h-4 ${savedIds.has(match.clinician_id) ? 'fill-cn-terracotta-500 text-cn-terracotta-500' : ''}`} />
                            </Button>
                            <Button
                              className="cn-btn cn-btn-ghost !py-2"
                              onClick={() => navigate('/patient/messages')}
                              data-testid={`message-btn-${index}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="cn-card p-12 text-center">
                  <Users className="w-12 h-12 text-cn-text-muted mx-auto mb-3" />
                  <h3 className="cn-display text-xl text-cn-text">No matches found</h3>
                  <p className="text-sm text-cn-text-muted mt-1">
                    {formData.in_network_only && allMatches.length > 0
                      ? 'None of your matches are in-network for your insurance. Turn off the in-network filter to see all matches, or add your insurance on your dashboard.'
                      : 'Try adjusting your criteria.'}
                  </p>
                </div>
              )}

              {results.rejected_clinicians?.length > 0 && (
                <div className="cn-card p-4 bg-cn-bg/40">
                  <p className="text-xs text-cn-text-muted flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5" /> {results.rejected_clinicians.length} clinicians didn&apos;t meet safety requirements
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="cn-card p-12 text-center bg-gradient-to-br from-white to-cn-bg/60" data-testid="find-empty-state">
              <div className="w-20 h-20 rounded-3xl bg-cn-primary/10 mx-auto flex items-center justify-center mb-4">
                <NeuralPulseOrb size={56} />
              </div>
              <h3 className="cn-display text-2xl text-cn-text dark:text-white">AI-powered matching</h3>
              <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">
                Fill the filters on the left and we&apos;ll surface clinicians ranked by clinical fit, safety, and availability.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Floating Compare bar */}
      {compareIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 rounded-2xl bg-cn-text text-white shadow-cn-floating" data-testid="compare-bar">
          <Columns3 className="w-4 h-4" />
          <span className="text-sm font-semibold">{compareIds.length} selected to compare</span>
          <Button onClick={() => setCompareOpen(true)} className="cn-btn !bg-white !text-cn-text hover:!bg-cn-bg !py-1.5 !px-4 text-sm" data-testid="open-compare-btn">
            Compare
          </Button>
          <button onClick={() => setCompareIds([])} className="text-white/70 hover:text-white" data-testid="clear-compare-btn" aria-label="Clear compare">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Compare drawer */}
      <Sheet open={compareOpen} onOpenChange={setCompareOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto" data-testid="compare-drawer">
          <SheetHeader>
            <SheetTitle className="cn-display text-2xl flex items-center gap-2"><Columns3 className="w-5 h-5 text-cn-primary" /> Compare clinicians</SheetTitle>
            <SheetDescription>Side-by-side on the metrics that matter for safe, well-matched care.</SheetDescription>
          </SheetHeader>
          <div className="mt-5 grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.max(compareList.length, 1)}, minmax(0, 1fr))` }}>
            {compareList.map((m, i) => (
              <div key={m.match_id} className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-5" data-testid={`compare-col-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cn-primary to-cn-secondary text-white flex items-center justify-center font-bold text-lg">
                    {m.clinician_name?.charAt(0) || 'C'}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-display font-bold text-cn-text dark:text-white truncate">{m.clinician_name}</h4>
                    <Badge variant="outline" className="border-cn-border">{m.license_type || 'RN'}</Badge>
                  </div>
                </div>
                <div className={`mt-4 text-center px-4 py-3 rounded-2xl ${getScoreClass(m.total_score)}`}>
                  <p className="font-display font-extrabold text-3xl leading-none">{m.total_score}%</p>
                  <p className="text-[10px] uppercase tracking-wider font-bold mt-1">Overall match</p>
                </div>
                <dl className="mt-4 space-y-2.5 text-sm">
                  {[
                    { l: 'Clinical fit', v: `${m.clinical_fit_score}%` },
                    { l: 'Complexity', v: `${m.complexity_alignment_score}%` },
                    { l: 'Availability', v: `${m.availability_score}%` },
                    { l: 'Experience', v: m.years_experience ? `${m.years_experience} yrs` : '—' },
                    { l: 'Distance', v: m.distance_miles != null ? (m.distance_miles < 1 ? '< 1 mi' : `${Math.round(m.distance_miles)} mi`) : '—' },
                    { l: 'Risk level', v: m.risk_level || '—' },
                    { l: 'Safety checks', v: m.safety_gates_passed ? 'Passed' : 'Review' },
                  ].map((row) => (
                    <div key={row.l} className="flex items-center justify-between border-b border-cn-border/50 pb-2">
                      <dt className="text-cn-text-muted">{row.l}</dt>
                      <dd className="font-semibold text-cn-text dark:text-white capitalize">{row.v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={() => { setCompareOpen(false); setNegotiateTarget({ id: m.clinician_id, first_name: m.clinician_name }); }}
                    className="flex-1 cn-btn cn-btn-primary !py-2 text-sm"
                    data-testid={`compare-negotiate-${i}`}
                  >
                    <Handshake className="w-4 h-4" /> Book
                  </Button>
                  <Button onClick={() => toggleCompare(m.clinician_id)} className="cn-btn cn-btn-ghost !py-2 text-sm" data-testid={`compare-remove-${i}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <NegotiateModal
        open={!!negotiateTarget}
        onOpenChange={(v) => !v && setNegotiateTarget(null)}
        clinician={negotiateTarget}
        token={token}
        onCreated={() => navigate('/patient/offers')}
      />
    </DashboardShell>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted block mb-1.5">{label}</label>
    {children}
  </div>
);
